import {
  Component,
  Input,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NlBiChartData } from '../../../models/chatbot.model';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartConfiguration,
} from 'chart.js';

// Register Chart.js modules (tree-shakeable)
Chart.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

const CHART_COLORS = ['#42B0D5', '#00243D', '#4BC0C0', '#FF9F40', '#9966FF', '#FF6384', '#FFCE56', '#4CAF50'];

@Component({
  selector: 'app-chart-renderer',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    @if (chartData) {
      <div class="chart-wrapper">
        @if (chartData.title) {
          <div class="chart-title">{{ chartData.title }}</div>
        }

        @switch (chartData.chart_type) {
          @case ('kpi_card') {
            <div class="kpi-card">
              <div class="kpi-value">{{ formatKpiValue(chartData.kpi_value) }}</div>
              @if (chartData.kpi_unit) {
                <div class="kpi-unit">{{ chartData.kpi_unit }}</div>
              }
              @if (chartData.kpi_label) {
                <div class="kpi-label">{{ chartData.kpi_label }}</div>
              }
            </div>
          }

          @case ('table') {
            <div class="chart-table-wrapper">
              @if (tableColumns.length > 0) {
                <table class="chart-table">
                  <thead>
                    <tr>
                      @for (col of tableColumns; track col) {
                        <th>{{ formatColumnName(col) }}</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of chartData.data; track $index) {
                      <tr>
                        @for (col of tableColumns; track col) {
                          <td>{{ row[col] }}</td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              }
            </div>
          }

          @default {
            <div class="canvas-wrapper">
              <canvas #chartCanvas></canvas>
            </div>
          }
        }
      </div>
    }
  `,
  styles: [
    `
      .chart-wrapper {
        margin-top: 12px;
        padding: 16px;
        background: var(--mds-color-neutral-50, #f8f9fa);
        border: 1px solid var(--mds-color-neutral-200, #e9ecef);
        border-radius: 8px;
      }

      .chart-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--mds-color-neutral-700, #495057);
        margin-bottom: 12px;
        text-align: center;
      }

      /* KPI Card */
      .kpi-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 24px 16px;
        gap: 4px;
      }

      .kpi-value {
        font-size: 48px;
        font-weight: 700;
        color: var(--mds-color-brand-500, #42b0d5);
        line-height: 1;
      }

      .kpi-unit {
        font-size: 18px;
        font-weight: 600;
        color: var(--mds-color-neutral-600, #6c757d);
      }

      .kpi-label {
        font-size: 14px;
        color: var(--mds-color-neutral-500, #868e96);
        margin-top: 4px;
      }

      /* Table */
      .chart-table-wrapper {
        overflow-x: auto;
        max-height: 320px;
        overflow-y: auto;
      }

      .chart-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;

        th {
          background: var(--mds-color-neutral-100, #f1f3f5);
          color: var(--mds-color-neutral-700, #495057);
          font-weight: 600;
          padding: 8px 12px;
          text-align: left;
          border-bottom: 2px solid var(--mds-color-neutral-300, #dee2e6);
          white-space: nowrap;
        }

        td {
          padding: 7px 12px;
          border-bottom: 1px solid var(--mds-color-neutral-200, #e9ecef);
          color: var(--mds-color-neutral-800, #343a40);
        }

        tr:last-child td {
          border-bottom: none;
        }

        tr:nth-child(even) td {
          background: var(--mds-color-neutral-50, #f8f9fa);
        }

        tr:hover td {
          background: var(--mds-color-neutral-100, #f1f3f5);
        }
      }

      /* Canvas chart */
      .canvas-wrapper {
        position: relative;
        height: 280px;
      }

      canvas {
        width: 100% !important;
      }
    `,
  ],
})
export class ChartRendererComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() chartData!: NlBiChartData;
  @ViewChild('chartCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  tableColumns: string[] = [];
  private chart?: Chart;
  private viewInitialized = false;

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.updateTableColumns();
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.viewInitialized) {
      this.destroyChart();
      this.updateTableColumns();
      setTimeout(() => this.renderChart(), 0);
    } else if (changes['chartData']?.firstChange) {
      this.updateTableColumns();
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private updateTableColumns(): void {
    if (this.chartData?.chart_type === 'table' && this.chartData.data?.length > 0) {
      this.tableColumns = Object.keys(this.chartData.data[0]);
    }
  }

  private renderChart(): void {
    if (!this.canvasRef?.nativeElement) return;
    if (!this.chartData?.data?.length) return;

    const type = this.chartData.chart_type;
    if (type === 'kpi_card' || type === 'table') return;

    const canvas = this.canvasRef.nativeElement;
    const config = this.buildChartConfig();
    if (config) {
      this.chart = new Chart(canvas, config);
    }
  }

  private buildChartConfig(): ChartConfiguration | null {
    const { chart_type, data, x_axis, y_axis, series } = this.chartData;

    if (chart_type === 'bar_chart' || chart_type === 'line_chart') {
      const xCol = x_axis?.column ?? Object.keys(data[0] ?? {})[0];
      const labels = data.map((row) => String(row[xCol] ?? ''));

      const datasets =
        series && series.length > 0
          ? series.map((s, i) => {
              const col = typeof s === 'string' ? s : s.column;
              const lbl = typeof s === 'string' ? this.formatColumnName(s) : s.label;
              return {
                label: lbl,
                data: data.map((row) => Number(row[col] ?? 0)),
                backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + (chart_type === 'bar_chart' ? '' : '33'),
                borderColor: CHART_COLORS[i % CHART_COLORS.length],
                borderWidth: 2,
                fill: chart_type === 'line_chart',
                tension: 0.3,
                pointRadius: 4,
              };
            })
          : [
              {
                label: y_axis?.label ?? 'Value',
                data: data.map((row) => {
                  const yCol = y_axis?.column ?? Object.keys(data[0] ?? {})[1];
                  return Number(row[yCol] ?? 0);
                }),
                backgroundColor: CHART_COLORS[0] + (chart_type === 'bar_chart' ? '' : '33'),
                borderColor: CHART_COLORS[0],
                borderWidth: 2,
                fill: chart_type === 'line_chart',
                tension: 0.3,
                pointRadius: 4,
              },
            ];

      return {
        type: chart_type === 'bar_chart' ? 'bar' : 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { maxRotation: 45, font: { size: 11 } },
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: { font: { size: 11 } },
            },
          },
        },
      } as ChartConfiguration;
    }

    if (chart_type === 'pie_chart') {
      // Pie data may have { label, value } or use x_axis/y_axis column keys
      const labelCol = x_axis?.column ?? 'label';
      const valueCol = y_axis?.column ?? 'value';
      const labels = data.map((row) => String(row[labelCol] ?? ''));
      const values = data.map((row) => Number(row[valueCol] ?? 0));

      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: CHART_COLORS.slice(0, data.length),
              borderWidth: 2,
              borderColor: '#fff',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right' },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
                  return ` ${ctx.label}: ${ctx.parsed.toLocaleString()} (${pct}%)`;
                },
              },
            },
          },
        },
      } as ChartConfiguration;
    }

    return null;
  }

  private destroyChart(): void {
    this.chart?.destroy();
    this.chart = undefined;
  }

  formatColumnName(col: string): string {
    return col
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  formatKpiValue(value: any): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
      return value % 1 === 0 ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(value);
  }
}
